import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { sendMessageApi } from "../../../chatApi";
import type { ChatMessage } from "../../../types";

// Always visible while a session is active - no "open chat" click needed.
// The server only pushes new_message to the OTHER participant, so the
// sender folds their own message into the shared stream/history via
// onSent (useTogetherChatMessages.ts's appendSentMessage), same as every
// other optimistic-send pattern in this app.
export function TogetherChatInput({
  conversationId,
  onSent,
  embedded,
  partnerName,
}: {
  conversationId: string | null;
  onSent: (message: ChatMessage) => void;
  // TogetherRoot.tsx renders this inside its own unified dock container
  // (alongside the reaction row) rather than as its own independent
  // floating bar - embedded drops the position/background/shape styling
  // below and just renders the form content, letting the parent own the
  // dock's outer chrome. Defaults to false (unchanged, self-positioned
  // standalone bar) since nothing else currently renders this component.
  embedded?: boolean;
  // Purely presentational ("Message Davido…" vs the generic "Message…") -
  // TogetherRoot.tsx already computes this from real session/presence data
  // for its own status pill, just threaded through as a prop here rather
  // than this component fetching/deriving its own copy of it.
  partnerName?: string;
}) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  async function handleSend(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !conversationId || sending) return;

    setSending(true);
    const result = await sendMessageApi(conversationId, trimmed);
    setSending(false);

    if (result.ok) {
      setText("");
      onSent(result.data);
    }
  }

  if (!conversationId) return null;

  const standaloneStyle: React.CSSProperties = {
    position: "fixed",
    left: 16,
    right: 16,
    bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
    zIndex: 1400,
    // Solid, not the shared .glass translucency - over Together Mode's
    // black video stage, the blurred 75%-opaque white washes out grey
    // instead of white (same issue AppDialog had against MUI's Backdrop
    // scrim).
    background: "var(--surface-opaque)",
    backdropFilter: "none",
  };

  return (
    <form
      onSubmit={handleSend}
      className={embedded ? "together-composer d-flex align-items-center gap-2 px-2 py-1" : "glass d-flex align-items-center gap-1 px-2 py-1"}
      style={embedded ? undefined : standaloneStyle}
    >
      <TextField
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={partnerName ? `Message ${partnerName}…` : "Message…"}
        size="small"
        fullWidth
        disabled={sending}
        className={embedded ? "together-composer-input" : undefined}
        sx={{ "& .MuiOutlinedInput-root": { background: "transparent" } }}
      />
      <IconButton
        type="submit"
        aria-label="Send message"
        size="small"
        disabled={!text.trim() || sending}
        className={embedded ? "together-send-btn" : undefined}
      >
        <i className={`bi bi-send-fill${embedded ? "" : " text-pink"}`} aria-hidden="true" />
      </IconButton>
    </form>
  );
}
